import {
    geoMercator,
    geoPath,
} from "d3-geo"
import {scaleQuantize} from "d3-scale"
import {select} from "d3-selection"
import {setSelectedWard} from "infraMonitor/projects-stats/actions"
import {
    formattedCurrency,
    number,
} from "localization"
import isEqual from "lodash.isequal"
import React from "react"
import {connect} from "react-redux"
import {withRouter} from "react-router-dom"
import styled from "styled-components"
import {
    getMun,
    getMunData,
} from "utils"

const municipality = getMun()
const subDomainData = getMunData()

const colorScale = val =>
    scaleQuantize()
        .domain([0, val])
        .range(["var(--color-map-1)", "var(--color-map-2)", "var(--color-map-3)", "var(--color-map-4)",])

let debounce

let geojson

if (municipality === "") {
    import(`../geojson/dhangadhi-geojson.json`).then(res => (geojson = res))
} else if (subDomainData.title) {
    import(`../geojson/${municipality}-geojson.json`).then(
        res => (geojson = res),
    ).catch(err => null)
}

class Map extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            parentWidth: 0,
        }
        this.ref = React.createRef()
    }

    componentDidMount() {
        this.fitParentContainer()
        window.addEventListener("resize", this.fitParentContainer)
    }

    fitParentContainer = () => {
        const {parentWidth} = this.state

        if (!this.ref.current) return;

        const currentParentWidth =
            this.ref.current && this.ref.current.getBoundingClientRect().width

        const shouldResize = parentWidth !== currentParentWidth
        if (shouldResize) {
            this.setState({
                parentWidth: currentParentWidth,
            })
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const {data, width, selectedLanguage} = nextProps
        const {parentWidth} = nextState

        return (
            !isEqual(data, this.props.data) ||
            width !== this.props.width ||
            parentWidth !== this.props.parentWidth ||
            selectedLanguage !== this.props.selectedLanguage
        )
    }

    onMouseEnter = (e, d) => {
        const {Name} = d.properties

        const {data} = this.props
        const value = data
            .filter(obj => `Ward ${obj.ward}` === Name)
            .map(obj => obj.value)
        const maxProjectNumber = Math.max(
            ...data.filter(d => d.ward <= this.getTotalWards()).map(d => d.value),
        )

        let color = colorScale(maxProjectNumber)


        select(e.target)
            .attr("stroke", "var(--color-map-stroke-hover)")
            .attr("stroke-width", "2")

        const layers = document.querySelectorAll('.geojson-layer path');

        layers.forEach(item => {
            if (item !== e.target) {
                item.classList.add('state-opacity');
            }
        })

        // // replace ward name 'Ward 1' with number '1'
        const ward = parseInt(Name.replace("Ward ", ""), 10)
        clearTimeout(debounce)
        debounce = setTimeout(() => {
            this.props.dispatchSelectedWard(ward)
        }, 500)

        // display cursor only on selected ward
        const checkWard = this.props.data.find(
            obj => parseInt(obj.ward, 10) === ward,
        )
        if (checkWard) {
            select(e.target).attr("cursor", "pointer")
        }
    }
    onMouseLeave = (e, fillColor) => {
        select(e.target)
            .attr("stroke", "#0e1724")
            .attr("stroke-width", "1")

        const layers = document.querySelectorAll('.geojson-layer path');

        layers.forEach(item => {
            if (item !== e.target) {
                item.classList.remove('state-opacity');
            }
        })

        clearTimeout(debounce)
        debounce = setTimeout(() => {
            this.props.dispatchSelectedWard("all")
        }, 500)
    }
    getColor = (d, color) => {
        const {Name} = d.properties
        const {data} = this.props
        const value = data
            .filter(obj => `Ward ${obj.ward}` === Name)
            .map(obj => obj.value)


        // const colorValue = color(value[0])
        // if (colorValue) {
        //     return colorValue.toString()
        // }
        // return "var(--color-map-5)"

        const wardValue = value[0]
        
        if (!wardValue || wardValue === 0) {
            return "var(--color-map-5)"
        }

        return color(wardValue).toString()
    }
    getValue = d => {
        const {Name} = d.properties
        const {data} = this.props
        const value = data
            .filter(obj => `Ward ${obj.ward}` === Name)
            .map(obj => obj.value)

        return value[0]
    }
    clickToView = d => {
        const { selectedLanguage } = this.props;
        if (selectedLanguage === "np") {
            return `<span><strong>वडा ${number(
                d.properties.Name.replace("Ward ", ""),
            )}</strong> हेर्नका लागि थिच्नुस ।</span>`
        }
        return `<span>Click to view <strong>Ward ${number(
            d.properties.Name.replace("Ward ", ""),
        )}</strong></span>`
    }

    getTotalWards = () => {
        const {municipality, municipalities} = this.props
        const currentMunicipality = municipalities.find(
            val => val.name === municipality.title_en,
        )
        return currentMunicipality.no_of_wards
    }

    render() {
        const {data, selectedDropDown, selectedLanguage, fiscalYear, projectType} = this.props
        const {parentWidth} = this.state

        const height = parentWidth
        const projection = geoMercator().fitExtent(
            [[0, 0], [parentWidth * 0.9, height * 0.9]],
            geojson,
        )
        const path = geoPath().projection(projection)

        // Functions to calculate text position
        const calculateXPosition = (d) => path.centroid(d)[0];
        const calculateYPosition = (d) => path.centroid(d)[1];

        const maxProjectNumber = Math.max(
            ...data.filter(d => d.ward <= this.getTotalWards()).map(d => d.value),
        )

        const color = colorScale(maxProjectNumber)

        if (!geojson) {
            return null
        }

        return (
            <MapSection className="map" innerRef={this.ref}>
                <div>
                    <svg width={parentWidth} height={height}>
                        <g className="geojson-layer">
                            {geojson.features.map(d => (
                                d.properties.Name.includes("Ward")
                                    ?
                                    <React.Fragment key={d.properties.Name}>
                                        <path
                                            key={d.properties.Name}
                                            d={path(d)}
                                            fill={this.getColor(d, color)}
                                            stroke="#0e1724"
                                            className={'cursor-pointer'}
                                            strokeWidth="1"
                                            strokeOpacity="0.5"
                                            onClick={e => {
                                                const selectedWard = d.properties.Name.replace("Ward ", "")

                                                 if (projectType === "local-project") {
                                                    this.props.history.push({
                                                        pathname: "/local-projects",
                                                        search: `?ward=${selectedWard}&fiscal-year=${fiscalYear}`,
                                                    });
                                                    return;
                                                }
    
                                                const checkWard = data.find(
                                                    obj => obj.ward === selectedWard,
                                                )

                                                if (checkWard) {
                                                    clearTimeout(debounce)
                                                    this.props.dispatchSelectedWard("all")
                                                    this.props.history.push({
                                                        pathname: "/projects",
                                                        search: `?ward=${selectedWard}&fiscal-year=${fiscalYear}`,
                                                    })
                                                }
                                            }}
                                            onMouseMove={e => {
                                                const value = this.getValue(d)

                                                const elem = document.getElementById("tooltip-map")

                                                let title

                                                if (selectedDropDown === "budget") {
                                                    title = selectedLanguage === "en" ? "NRs. " : "नेरु "
                                                } else {
                                                    title = value > 1 ? "Projects: " : "Project: "
                                                    if (selectedLanguage === "np") {
                                                        title = value > 1 ? "परियोजनाहरू: " : "परियोजना: "
                                                    }
                                                }
                                                const displayValue = value === 0 ? "N/A" : formattedCurrency(value);
                                                const clickToViewContent = this.clickToView(d);

                                                elem.innerHTML = `
                                                <div class="tooltip" >
                                                <div class="tooltip-arrow"></div>
                                                    <div>
                                                    <span class="title">${
                                                    selectedLanguage === "en"
                                                        ? "Ward: "
                                                        : "वडा: "
                                                }</span>
                                                    <span class="total-count">${number(
                                                    d.properties.Name.replace("Ward ", ""),
                                                )}</span>
                                                    </div>
                                                    <div>
                                                    <span class="title">${
                                                    value ? title : ""
                                                }</span>
                                                    <span class="total-count">${value === 0 || value == null ? "N/A" : formattedCurrency(value)}</span>
                                                    </div>
                                                   ${clickToViewContent ? `<div class="view-map-detail">${clickToViewContent}</div>` : ''}
                                                </div>
                        `

                                                elem.style.cssText = "opacity:1;  position: absolute;;"
                                                elem.style.display = "block"
                                                elem.style.top = `${e.pageY - 190}px`
                                                if (selectedLanguage === "en") {
                                                    elem.style.left = `${e.pageX - 192}px`
                                                }
                                                if (selectedLanguage === "np") {
                                                    elem.style.left = `${e.pageX - 205}px`
                                                }
                                            }}
                                            onMouseEnter={e => {
                                                this.onMouseEnter(e, d)
                                                const wardNumber = d.properties.Name.replace("Ward ", "")
                                                const wardData = this.props.data.find(item => item.ward == wardNumber)
                                                if (wardData && this.props.onWardHover) {
                                                    this.props.onWardHover(wardData)
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                const elem = document.getElementById("tooltip-map")
                                                elem.style.display = "none"
                                                const fillColor = this.getColor(d, color)
                                                this.onMouseLeave(e, fillColor)
                                                  if (this.props.onWardLeave) {
                                                    this.props.onWardLeave()
                                                }
                                            }}
                                        />
                                        <text fill="#fff" x={`${calculateXPosition(d)}`} y={`${calculateYPosition(d)}`}>
                                                {number(d.properties.Name.replace("Ward ", ""))}
                                        </text>
                                    </React.Fragment>
                                    :
                                    <React.Fragment key={d.properties.Name}>
                                        <path
                                            key={d.properties.Name}
                                            d={path(d)}
                                            fill="#2b6333"
                                            stroke="#0e1724"
                                            strokeWidth="1"
                                            strokeOpacity="0.5"
                                        />
                                        <text fill="#fff" x={`${calculateXPosition(d)}`} y={`${calculateYPosition(d)}`}>
                                        {this.getValue(d) ? formattedCurrency(this.getValue(d)) : formattedCurrency(0)}
                                        </text>
                                    </React.Fragment>
                            ))}
                        </g>
                    </svg>
                </div>
                <div id="tooltip-map"/>
            </MapSection>
        )
    }
}

const MapSection = styled.div`
  /* width: 100%; */
`
const mapStateToProps = state => ({
    selectedLanguage: state.localizationReducers.selectedLanguage,
    fiscalYear: state.fiscalYearReducers.selectedFiscalYear,
    municipalities: state.staticData.municipalities,
})
const mapDispatchToProps = dispatch => ({
    dispatchSelectedWard: selectedWard => dispatch(setSelectedWard(selectedWard)),
})

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps,
    )(Map),
)



// WEBPACK FOOTER //
// ./src/components/visualization/map/map.js